import type { AnalyticsBrowser } from '@segment/analytics-next';

import { DEFAULT_SEGMENT_CDN_ORIGIN } from '../shared/segment';

export interface AnalyticsConfig {
  writeKey?: string;
}

export interface AnalyticsProvider {
  init(config: AnalyticsConfig): void;
  page(name: string, properties?: Record<string, unknown>): Promise<void>;
  track(event: string, properties?: Record<string, unknown>): Promise<void>;
}

/**
 * Segment-backed provider. The `@segment/analytics-next` bundle is loaded
 * lazily via dynamic import so non-receipts routes (and any environment
 * without a write key) don't pay the bundle cost up front.
 *
 * All Segment calls are isolated in try/catch — analytics is best-effort and
 * must never reject to UI code or surface unhandled rejections.
 */
/**
 * `AnalyticsBrowser` is itself `PromiseLike<[Analytics, Context]>`, so passing
 * it through `Promise.then` auto-unwraps it to the tuple. We wrap the client
 * in a plain object so the promise machinery doesn't follow its `then` method.
 */
type ClientHolder = { client: AnalyticsBrowser };

export class SegmentAnalyticsProvider implements AnalyticsProvider {
  private clientPromise: Promise<ClientHolder | null> = Promise.resolve(null);

  init(config: AnalyticsConfig): void {
    if (!config.writeKey) {
      this.clientPromise = Promise.resolve(null);
      return;
    }
    const writeKey = config.writeKey;
    const cdnURL = import.meta.env.VITE_SEGMENT_API_BASE_URL || DEFAULT_SEGMENT_CDN_ORIGIN;
    this.clientPromise = import('@segment/analytics-next')
      .then(({ AnalyticsBrowser }) => ({ client: AnalyticsBrowser.load({ writeKey, cdnURL }) }))
      .catch((err): null => {
        console.warn('[analytics] failed to load @segment/analytics-next', err);
        return null;
      });
  }

  async page(name: string, properties?: Record<string, unknown>): Promise<void> {
    try {
      const holder = await this.clientPromise;
      if (holder) {
        // The real Segment call is being made. If no error is logged after
        // this, the call went through and the analytics path is working.
        console.log('[analytics] Segment page() sending', { name, properties });
        await holder.client.page(name, properties);
      } else {
        // No client (no write key, or the Segment bundle failed to load): the
        // app still made the call, it just had nowhere to send it. Logging this
        // makes the attempt visible during local testing without a write key.
        console.log('[analytics] Segment page() skipped — no client', { name, properties });
      }
    } catch (err) {
      console.warn('[analytics] page() failed', err);
    }
  }

  async track(event: string, properties?: Record<string, unknown>): Promise<void> {
    try {
      const holder = await this.clientPromise;
      if (holder) {
        // The real Segment call is being made. If no error is logged after
        // this, the call went through and the analytics path is working.
        console.log('[analytics] Segment track() sending', { event, properties });
        await holder.client.track(event, properties);
      } else {
        // No client (no write key, or the Segment bundle failed to load): the
        // app still made the call, it just had nowhere to send it. Logging this
        // makes the attempt visible during local testing without a write key.
        console.log('[analytics] Segment track() skipped — no client', { event, properties });
      }
    } catch (err) {
      console.warn('[analytics] track() failed', err);
    }
  }
}

export class NoOpAnalyticsProvider implements AnalyticsProvider {
  init(): void {
    console.info('[analytics] NoOp provider initialized (no write key configured)');
  }
  page(name: string, properties?: Record<string, unknown>): Promise<void> {
    console.info('[analytics] NoOp page()', { name, properties });
    return Promise.resolve();
  }
  track(event: string, properties?: Record<string, unknown>): Promise<void> {
    console.info('[analytics] NoOp track()', { event, properties });
    return Promise.resolve();
  }
}

/**
 * Analytics singleton that starts as a no-op proxy and delegates to the
 * real provider once init() is called. This avoids reading env vars at
 * module scope and keeps the provider selection in main.tsx.
 */
class AnalyticsProxy implements AnalyticsProvider {
  private provider: AnalyticsProvider = new NoOpAnalyticsProvider();

  init(config: AnalyticsConfig): void {
    if (config.writeKey) {
      this.provider = new SegmentAnalyticsProvider();
      console.info('[analytics] Segment provider initialized');
    } else {
      this.provider = new NoOpAnalyticsProvider();
    }
    this.provider.init(config);
  }

  page(name: string, properties?: Record<string, unknown>): Promise<void> {
    return this.provider.page(name, properties);
  }

  track(event: string, properties?: Record<string, unknown>): Promise<void> {
    return this.provider.track(event, properties);
  }
}

export const analytics: AnalyticsProvider = new AnalyticsProxy();
